/**
 * /contacto — Página de contacto pública.
 *
 * Punto único de contacto para usuarios + revisores (Culqi onboarding lo
 * exige). Reúne email, teléfono, WhatsApp y horario de atención. Los emails
 * específicos (privacidad@, reembolsos@) viven en sus páginas legales — acá
 * solo el correo general y el canal directo.
 *
 * No usa form: para reclamos formales redirige a /reclamaciones (libro
 * Indecopi) y para soporte general da los canales directos.
 */

import { Mail, MessageCircle, Phone, Clock, MapPin, ScrollText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LegalSection, LegalShell } from '@/components/layout/LegalShell';
import { contact } from '@/lib/contact';

export function ContactPage() {
  return (
    <LegalShell
      eyebrow="Soporte"
      title="Contáctanos"
      updated={`Atención de ${contact.supportHours.toLowerCase()}.`}
    >
      <LegalSection title="Canales directos">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ContactCard
            icon={<Mail size={18} strokeWidth={1.8} />}
            label="Correo general"
            value={contact.supportEmail}
            href={`mailto:${contact.supportEmail}`}
            hint="Respondemos en < 24 h hábiles."
          />
          <ContactCard
            icon={<Phone size={18} strokeWidth={1.8} />}
            label="Teléfono"
            value={contact.phoneDisplay}
            href={`tel:${contact.phoneTel}`}
            hint="Lun–Vie 9:00–18:00."
          />
          <ContactCard
            icon={<MessageCircle size={18} strokeWidth={1.8} />}
            label="WhatsApp"
            value={contact.phoneDisplay}
            href={`https://wa.me/${contact.phoneWa}`}
            external
            hint="Soporte rápido en horario hábil."
          />
          <ContactCard
            icon={<Clock size={18} strokeWidth={1.8} />}
            label="Horario"
            value={contact.supportHours}
            hint="Fines de semana respondemos por correo."
          />
        </div>
      </LegalSection>

      <LegalSection title="Correos específicos">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Privacidad y datos personales (Ley 29733):</strong>{' '}
            <a
              href={`mailto:${contact.privacyEmail}`}
              className="underline-offset-2 hover:underline"
              style={{ color: 'var(--accent)' }}
            >
              {contact.privacyEmail}
            </a>
          </li>
          <li>
            <strong>Reembolsos y facturación:</strong>{' '}
            <a
              href={`mailto:${contact.refundsEmail}`}
              className="underline-offset-2 hover:underline"
              style={{ color: 'var(--accent)' }}
            >
              {contact.refundsEmail}
            </a>
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="Reclamos formales">
        <p>
          Para presentar un <strong>reclamo o queja formal</strong> conforme a la Ley 29571 (Código
          de Protección y Defensa del Consumidor), usa nuestro libro de reclamaciones virtual. Te
          responderemos en máximo 30 días calendario.
        </p>
        <p className="mt-3">
          <Link
            to="/reclamaciones"
            className="inline-flex h-9 items-center gap-1.5 rounded-[var(--radius)] border px-3 text-sm no-underline transition-colors hover:bg-[var(--surface-2)]"
            style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
          >
            <ScrollText size={14} strokeWidth={1.8} /> Ir al Libro de Reclamaciones
          </Link>
        </p>
      </LegalSection>

      <LegalSection title="Datos del responsable">
        <div className="flex items-start gap-2">
          <MapPin size={16} strokeWidth={1.8} style={{ color: 'var(--accent)' }} className="mt-0.5" />
          <div>
            <p>
              <strong>{contact.legalName}</strong>
              {contact.legalRuc && (
                <>
                  {' '}— RUC <span className="tabular">{contact.legalRuc}</span>
                </>
              )}
            </p>
            <p className="mt-0.5">{contact.legalAddress}</p>
            <p className="mt-1 text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
              Operamos íntegramente en línea. No atendemos en oficina física — todo el servicio se
              brinda vía la plataforma web. Para gestiones presenciales coordínalas previamente por
              correo.
            </p>
          </div>
        </div>
      </LegalSection>
    </LegalShell>
  );
}

/** Tarjeta visual de canal de contacto — clickable cuando hay `href`. */
function ContactCard({
  icon,
  label,
  value,
  href,
  hint,
  external,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
  hint?: string;
  external?: boolean;
}) {
  const inner = (
    <div
      className="flex items-start gap-3 rounded-[var(--radius-md)] border p-4 transition-colors"
      style={{
        background: 'var(--surface-1)',
        borderColor: 'var(--border-default)',
      }}
    >
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-sm)]"
        style={{ background: 'var(--surface-2)', color: 'var(--accent)' }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="text-[10.5px] uppercase tracking-[0.18em]"
          style={{ color: 'var(--text-muted)' }}
        >
          {label}
        </div>
        <div
          className="mt-0.5 truncate text-[14.5px]"
          style={{ color: 'var(--text-primary)', fontWeight: 600 }}
        >
          {value}
        </div>
        {hint && (
          <div className="mt-1 text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
            {hint}
          </div>
        )}
      </div>
    </div>
  );

  if (href) {
    return (
      <a
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        className="block no-underline"
      >
        {inner}
      </a>
    );
  }
  return inner;
}
